import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import type { AppState } from '../../../lib/store';
import { useRescueContext } from '../../lib/RescueContext';
import { US_STATES } from '../../lib/utils';
import {
  rescueCreateNanoOrderMutation,
  RescueCreateNanoOrderData,
  RescueCreateNanoOrderInput,
} from '../../lib/rescueQueries';
import { trackChipsOrderSubmitted } from '../../analytics';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { FormGrid } from '../../components/FormField/FormGrid';
import Button from '../../components/Button/Button';
import styles from './Chips.module.scss';

const CHIPS_PER_BOX = 20;

// Box-count is the canonical unit: backend takes `boxQuantity` and bounds it
// 1–25. Chip counts shown to the user are derived (`boxes * CHIPS_PER_BOX`).
const BOX_PRESETS = ['1', '2', '3', '4', '5'] as const;
const QUANTITY_PRESETS = [...BOX_PRESETS, 'Other'] as const;
type QuantityChoice = (typeof QUANTITY_PRESETS)[number];
const DEFAULT_QUANTITY: QuantityChoice = '3';
const CUSTOM_BOX_MIN = 6;
const CUSTOM_BOX_MAX = 25;
const CUSTOM_BOX_STEP = 1;

/** Pragmatic email shape check — backend remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface OrderNanosModalProps {
  open: boolean;
  onClose: () => void;
}

interface SuccessState {
  orderId: string;
  boxCount: number;
}

export default function OrderNanosModal({ open, onClose }: OrderNanosModalProps) {
  const { orgName, shippingAddress } = useRescueContext();
  const session = useSelector((state: AppState) => state.session);
  const [createOrder] = useMutation<RescueCreateNanoOrderData, { input: RescueCreateNanoOrderInput }>(
    rescueCreateNanoOrderMutation,
  );

  // Only pre-fill from the rescue's stored address when it's actually a clean
  // US address with a 2-letter state code that matches our `<select>` options.
  const validUSAddress = useMemo(() => {
    if (!shippingAddress) {
      return null;
    }
    if (shippingAddress.country !== 'US') {
      return null;
    }
    if (!shippingAddress.state || !US_STATES.includes(shippingAddress.state)) {
      return null;
    }
    return shippingAddress;
  }, [shippingAddress]);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState(session?.email ?? '');
  const [quantityChoice, setQuantityChoice] = useState<QuantityChoice>(DEFAULT_QUANTITY);
  const [customBoxCount, setCustomBoxCount] = useState(CUSTOM_BOX_MIN);
  const [street, setStreet] = useState(validUSAddress?.line1 ?? '');
  const [apt, setApt] = useState(validUSAddress?.line2 ?? '');
  const [city, setCity] = useState(validUSAddress?.city ?? '');
  const [usState, setUsState] = useState(validUSAddress?.state ?? '');
  const [zip, setZip] = useState(validUSAddress?.zip ?? '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Re-seed the address fields once per open cycle so org-info that resolves
  // after the modal mounts still pre-fills the form. Mirrors OrderDevicesModal.
  const seededAddressRef = useRef(false);
  const seededEmailRef = useRef(false);
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededAddressRef.current = false;
      return;
    }
    if (validUSAddress && !seededAddressRef.current) {
      setStreet(validUSAddress.line1 ?? '');
      setApt(validUSAddress.line2 ?? '');
      setCity(validUSAddress.city ?? '');
      setUsState(validUSAddress.state ?? '');
      setZip(validUSAddress.zip ?? '');
      seededAddressRef.current = true;
    }
  }, [open, validUSAddress]);
  useEffect(() => {
    if (!open) {
      seededEmailRef.current = false;
      return;
    }
    if (session?.email && !seededEmailRef.current) {
      setContactEmail(session.email);
      seededEmailRef.current = true;
    }
  }, [open, session?.email]);

  function resetForm() {
    setContactName('');
    setContactEmail(session?.email ?? '');
    setQuantityChoice(DEFAULT_QUANTITY);
    setCustomBoxCount(CUSTOM_BOX_MIN);
    setStreet(validUSAddress?.line1 ?? '');
    setApt(validUSAddress?.line2 ?? '');
    setCity(validUSAddress?.city ?? '');
    setUsState(validUSAddress?.state ?? '');
    setZip(validUSAddress?.zip ?? '');
    setNotes('');
    setShowErrors(false);
    setSubmitError(null);
    setSubmitting(false);
    setSuccess(null);
    submittingRef.current = false;
  }

  function handleClose() {
    if (submittingRef.current) {
      return;
    }
    resetForm();
    onClose();
  }

  const isOther = quantityChoice === 'Other';
  const boxCount = isOther ? customBoxCount : Number.parseInt(quantityChoice, 10);
  const chipCount = boxCount * CHIPS_PER_BOX;
  const trimmedEmail = contactEmail.trim();

  const errors = {
    contactName: contactName.trim() === '',
    contactEmailMissing: trimmedEmail === '',
    contactEmailInvalid: trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail),
    boxCount: !Number.isFinite(boxCount) || boxCount <= 0,
    street: street.trim() === '',
    city: city.trim() === '',
    state: usState === '',
    zip: zip.trim() === '',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  async function handleSubmit() {
    if (submittingRef.current) {
      return;
    }
    if (hasErrors) {
      setSubmitError(null);
      setShowErrors(true);
      return;
    }
    submittingRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await createOrder({
        variables: {
          input: {
            contactName: contactName.trim(),
            contactEmail: trimmedEmail,
            boxQuantity: boxCount,
            shippingAddress: {
              line1: street.trim(),
              line2: apt.trim() ? apt.trim() : null,
              city: city.trim(),
              state: usState,
              country: 'US',
              zip: zip.trim(),
            },
            notes: notes.trim() ? notes.trim() : null,
          },
        },
      });
      const data = result.data?.rescueCreateNanoOrder;
      if (!data?.ok) {
        throw new Error('Order submission was not accepted');
      }
      trackChipsOrderSubmitted(chipCount);
      setSuccess({ orderId: data.orderId, boxCount });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order request');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function decrementCustom() {
    setCustomBoxCount((prev) => Math.max(CUSTOM_BOX_MIN, prev - CUSTOM_BOX_STEP));
  }

  function incrementCustom() {
    setCustomBoxCount((prev) => Math.min(CUSTOM_BOX_MAX, prev + CUSTOM_BOX_STEP));
  }

  if (success) {
    const { orderId, boxCount: confirmedBoxes } = success;
    const confirmedChips = confirmedBoxes * CHIPS_PER_BOX;
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <div className={styles.orderConfirmState}>
          <div className={styles.orderConfirmIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12.5L10 17.5L19 7.5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.orderConfirmTitle}>Request Submitted</h2>
          <p className={styles.orderConfirmDesc}>
            Your order for{' '}
            <strong>
              {confirmedChips} Fi Nano microchip{confirmedChips === 1 ? '' : 's'} ({confirmedBoxes} box
              {confirmedBoxes === 1 ? '' : 'es'})
            </strong>{' '}
            has been submitted for approval. You'll receive an email confirmation once approved.
          </p>
          <div className={styles.orderConfirmFooter}>
            <div className={styles.orderConfirmOrderNumber}>Order #{orderId}</div>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const customChipDisplay = customBoxCount * CHIPS_PER_BOX;

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Order Fi Nano Microchips</ModalTitle>
      <p className={styles.modalDescription}>
        Order free Fi Nano microchip boxes for <strong>{orgName || 'your rescue'}</strong>. Chips come in boxes of 20.
      </p>

      <FormGrid>
        <PortalField
          label="Contact Name *"
          error="Please provide a contact name"
          showError={showErrors && errors.contactName}
        >
          <PortalInput
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Full name"
            hasError={showErrors && errors.contactName}
          />
        </PortalField>
        <PortalField
          label="Contact Email *"
          error={errors.contactEmailMissing ? 'Please provide a contact email' : 'Please provide a valid email address'}
          showError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
        >
          <PortalInput
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@example.org"
            hasError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
          />
        </PortalField>
      </FormGrid>

      <PortalField label="Quantity *" hint={`${chipCount} chips = ${boxCount} box${boxCount === 1 ? '' : 'es'}`}>
        <ToggleGroup
          options={QUANTITY_PRESETS.map((q) => ({
            value: q,
            label: q === 'Other' ? 'Other' : String(Number.parseInt(q, 10) * CHIPS_PER_BOX),
          }))}
          value={quantityChoice}
          onChange={(v) => setQuantityChoice(v as QuantityChoice)}
        />
        {isOther && (
          <div className={styles.customQuantityRow}>
            <Button
              variant="ghost"
              size="sm"
              onClick={decrementCustom}
              disabled={customBoxCount <= CUSTOM_BOX_MIN}
              className={styles.customQuantityButton}
              aria-label="Decrease quantity"
            >
              &minus;
            </Button>
            <span className={styles.customQuantityValue}>{customChipDisplay}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={incrementCustom}
              disabled={customBoxCount >= CUSTOM_BOX_MAX}
              className={styles.customQuantityButton}
              aria-label="Increase quantity"
            >
              +
            </Button>
          </div>
        )}
      </PortalField>

      <div className={styles.orderSectionHeading}>Shipping Address</div>
      <FormGrid>
        <PortalField
          label="Street Address *"
          fullWidth
          error="Please provide a street address"
          showError={showErrors && errors.street}
        >
          <PortalInput
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="123 Main Street"
            hasError={showErrors && errors.street}
          />
        </PortalField>
        <PortalField label="Apt / Suite">
          <PortalInput value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt / Suite" />
        </PortalField>
        <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.city}>
          <PortalInput
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            hasError={showErrors && errors.city}
          />
        </PortalField>
        <PortalField label="State *" error="Please select a state" showError={showErrors && errors.state}>
          <PortalSelect
            value={usState}
            onChange={(e) => setUsState(e.target.value)}
            hasError={showErrors && errors.state}
          >
            <option value="">Select…</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </PortalSelect>
        </PortalField>
        <PortalField label="Zip Code *" error="Please provide a zip code" showError={showErrors && errors.zip}>
          <PortalInput
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="ZIP"
            inputMode="numeric"
            hasError={showErrors && errors.zip}
          />
        </PortalField>
      </FormGrid>

      <PortalField label="Notes">
        <PortalTextarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional — e.g., event coming up, urgent need"
        />
      </PortalField>

      {submitError && <div className={styles.errorContainer}>{submitError}</div>}

      <ModalActions>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Request'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
